/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { ShoppingBag, Search, Filter, Lock } from 'lucide-react';
import { Product } from '../../types/products';
import { useProducts } from '../../hooks/useProducts';
import { ProductCard } from './storefront/ProductCard';
import { Cart } from './storefront/Cart';
import { Checkout } from './storefront/Checkout';
import { AnimatePresence } from 'framer-motion';
import { GhostBrandIcon } from '../ui/PartnerIcons';

export const StorefrontView: React.FC = () => {
    const { products } = useProducts();
    const [cartOpen, setCartOpen] = useState(false);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [cartItems, setCartItems] = useState<{ product: Product, quantity: number }[]>([]);
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('All');

    const addToCart = (product: Product) => {
        if (product.isNftGated) {
            alert("This item requires specific NFT ownership to purchase.");
            return;
        }
        setCartItems(prev => {
            const existing = prev.find(item => item.product.id === product.id);
            if (existing) {
                return prev.map(item => item.product.id === product.id ? { ...item, quantity: item.quantity + 1 } : item);
            }
            return [...prev, { product, quantity: 1 }];
        });
        setCartOpen(true);
    };

    const removeFromCart = (productId: string) => {
        setCartItems(prev => prev.filter(item => item.product.id !== productId));
    };

    const updateQuantity = (productId: string, delta: number) => {
        setCartItems(prev => prev.map(item => {
            if (item.product.id === productId) {
                const newQuantity = Math.max(1, item.quantity + delta);
                return { ...item, quantity: newQuantity };
            }
            return item;
        }));
    };

    const filteredProducts = products.filter(p => 
        (category === 'All' || p.category === category) &&
        (p.name.toLowerCase().includes(search.toLowerCase()) || p.description?.toLowerCase().includes(search.toLowerCase()))
    );

    const categories = ['All', ...Array.from(new Set(products.map(p => p.category)))];

    return (
        <div className="min-h-screen bg-[#F8F9FA] dark:bg-[#0A0B0F] text-[#0A0B0F] dark:text-white">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-white/80 dark:bg-[#0A0B0F]/90 backdrop-blur-md border-b border-gray-200 dark:border-white/10 px-6 py-4 flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <div className="w-10 h-10 flex items-center justify-center">
                        <GhostBrandIcon className="w-full h-full" />
                    </div>
                    <span className="font-serif text-lg font-bold tracking-wide hidden sm:block">Ghost Store</span>
                </div>

                <div className="flex-1 max-w-md mx-6 hidden md:block">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <input 
                            type="text" 
                            placeholder="Search products..." 
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-white/5 border-none rounded-full focus:ring-1 focus:ring-nobel-gold outline-none transition-all"
                        />
                    </div>
                </div>

                <button 
                    onClick={() => setCartOpen(true)} 
                    className="relative p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                >
                    <ShoppingBag size={24} />
                    {cartItems.length > 0 && (
                        <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white dark:border-[#0A0B0F]">
                            {cartItems.reduce((acc, item) => acc + item.quantity, 0)}
                        </span>
                    )}
                </button>
            </header>

            {/* Hero / Banner */}
            <div className="bg-[#0A0B0F] text-white py-16 px-6 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-nobel-gold opacity-10 blur-[100px] rounded-full transform translate-x-1/3 -translate-y-1/3"></div>
                <div className="container mx-auto relative z-10 text-center">
                    <h1 className="font-serif text-4xl md:text-6xl font-bold mb-4">The Future of Commerce</h1>
                    <p className="text-gray-400 max-w-xl mx-auto mb-8">Secure, private, and decentralized. Shop with crypto instantly using GhostPay.</p>
                </div>
            </div>

            {/* Categories */}
            <div className="container mx-auto px-6 py-8 overflow-x-auto no-scrollbar">
                <div className="flex gap-2 justify-center min-w-max">
                    {categories.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setCategory(cat)}
                            className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${category === cat ? 'bg-[#0A0B0F] dark:bg-white text-white dark:text-black' : 'bg-gray-200 dark:bg-white/10 hover:bg-gray-300 dark:hover:bg-white/20'}`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            </div>

            {/* Product Grid */}
            <div className="container mx-auto px-6 pb-24">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredProducts.map(product => (
                        <div key={product.id} className="relative group">
                            {product.isNftGated && (
                                <div className="absolute top-4 right-4 z-20 bg-black/80 text-white p-1.5 rounded-full border border-white/20 shadow-lg" title="NFT Required">
                                    <Lock size={14} />
                                </div>
                            )}
                            <ProductCard product={product} onAdd={addToCart} />
                        </div>
                    ))}
                </div>
                {filteredProducts.length === 0 && (
                    <div className="text-center py-20 text-gray-500">
                        <Filter size={48} className="mx-auto mb-4 opacity-50"/>
                        <p>No products found matching your criteria.</p>
                    </div>
                )}
            </div>

            {/* Modals/Drawers */}
            <AnimatePresence>
                {cartOpen && (
                    <Cart 
                        items={cartItems} 
                        onClose={() => setCartOpen(false)} 
                        onRemove={removeFromCart}
                        onUpdateQuantity={updateQuantity}
                        onCheckout={() => {
                            setCartOpen(false);
                            setCheckoutOpen(true);
                        }}
                    />
                )}
                {checkoutOpen && (
                    <Checkout 
                        items={cartItems}
                        onClose={() => setCheckoutOpen(false)}
                        onSuccess={() => {
                            setCheckoutOpen(false);
                            setCartItems([]);
                        }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};